import { type VariantProps, cva } from "class-variance-authority";
import React, { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/cn";

// Upgraded modal implementation using Radix UI Dialog for better accessibility

// Legacy trigger and close components for compatibility
interface TriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const Trigger = ({ children, ...props }: TriggerProps) => {
  // These are now handled by Dialog components, but keeping for backwards compatibility
  return <button {...props}>{children}</button>;
};

interface RootProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

const Root = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: RootProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children}
    </Dialog>
  );
};

// DialogContent supplies the surface, padding and enter/exit animation; the
// panel adds a short rise on open and the width presets.
export const panelVariants = cva(
  "bg-card transition data-[state=open]:slide-in-from-bottom-2",
  {
    variants: {
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        auto: "max-w-fit",
      },
    },
    defaultVariants: {
      size: "lg",
    },
  },
);

type PanelProps = React.ComponentProps<"div"> &
  VariantProps<typeof panelVariants> & {
    children: React.ReactNode;
    showCloseButton?: boolean;
  };

const Panel = ({ size, children, className, ...props }: PanelProps) => {
  return (
    <DialogContent
      {...props}
      className={cn(panelVariants({ size }), className)}
    >
      {children}
    </DialogContent>
  );
};

interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

const Title = ({ children, className, ...props }: TitleProps) => {
  return (
    <DialogTitle
      {...props}
      className={cn("font-heading text-lg tracking-tight", className)}
    >
      {children}
    </DialogTitle>
  );
};

interface DescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

const Description = ({ children, className, ...props }: DescriptionProps) => {
  return (
    <DialogDescription
      {...props}
      className={cn("text-sm text-muted-foreground", className)}
    >
      {children}
    </DialogDescription>
  );
};

/**
 * Action area that stays on screen when a tall panel scrolls (short screens):
 * it sticks to the bottom of the panel's scroll area and the content scrolls
 * underneath. The negative offsets span the panel's p-6, since sticky insets
 * are measured from the scroll container's padding edge.
 */
const Footer = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        "sticky -bottom-6 -mx-6 -mb-6 flex flex-col gap-4 border-t bg-card px-6 pt-4 pb-6",
        className,
      )}
    />
  );
};

/** The standard modal: Root, Trigger, Panel, Title, Description and Footer, with width presets. */
export const Modal = {
  Root,
  Trigger,
  Panel,
  Title,
  Description,
  Footer,
};
